import React, { useCallback, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Star } from "lucide-react";

import NavBar from "@/components/NavBar";
import TaskHeaderCard from "@/components/TaskHeaderCard";
import TaskActionBar from "@/components/TaskActionBar";
import InvoiceCard from "@/components/InvoiceCard";
import PhotoTimeline from "@/components/PhotoTimeline";
import PhotoUploader from "@/components/PhotoUploader";
import RatingPanel from "@/components/RatingPanel";
import ChatPanel from "@/components/ChatPanel";
import ReceiptCard from "@/components/ReceiptCard";
import DisputeCard from "@/components/DisputeCard";
import AdminAssignAgentCard from "@/components/AdminAssignAgentCard";
import AgentTrustCard from "@/components/AgentTrustCard";
import ReferencePhotos from "@/components/ReferencePhotos";

import { useAuth } from "@/contexts/AuthContext";
import useTaskData from "@/hooks/useTaskData";
import useTaskWebSocket from "@/hooks/useTaskWebSocket";
import api, { formatApiError } from "@/lib/api";

const AGENT_LIVE_STATUSES = ["assigned", "in_progress", "awaiting_approval"];

export default function TaskDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [busy, setBusy] = useState(false);

  const sessionId = params.get("session_id");

  const handleSessionConsumed = useCallback(() => {
    params.delete("session_id");
    params.delete("pay");
    setParams(params, { replace: true });
  }, [params, setParams]);

  const {
    task,
    setTask,
    photos,
    setPhotos,
    messages,
    setMessages,
    error,
  } = useTaskData({ taskId: id, sessionId, onSessionConsumed: handleSessionConsumed });

  const [refPhotosSignal, setRefPhotosSignal] = useState(0);

  const handleIncomingMessage = useCallback(
    (payload) => {
      if (payload?.type === "task_updated" && payload.data) {
        setTask((prev) => (prev && prev.task_id === payload.data.task_id ? payload.data : prev));
        return;
      }
      if (payload?.type === "reference_photo_added" || payload?.type === "reference_photo_removed") {
        setRefPhotosSignal((n) => n + 1);
        return;
      }
      if (payload?.type !== "message" || !payload.data) return;
      setMessages((prev) => {
        if (prev.some((m) => m.message_id === payload.data.message_id)) return prev;
        return [...prev, payload.data];
      });
    },
    [setMessages, setTask]
  );

  const wsRef = useTaskWebSocket({
    taskId: task?.task_id,
    enabled: !!task,
    onMessage: handleIncomingMessage,
  });

  if (!task) {
    return (
      <div className="min-h-screen">
        <NavBar />
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 text-[var(--eb-text-dim)]">
          {error || "Loading task…"}
        </div>
      </div>
    );
  }

  const isClient = user?.user_id === task.client_id;
  const isAssignedAgent = user?.user_id === task.agent_id;
  const role = user?.role;

  const onStatusChange = async (newStatus) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/tasks/${id}/status`, { status: newStatus });
      setTask(data);
      toast.success(`Status: ${newStatus.replace("_", " ")}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const onFundEscrow = async (kind) => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/mock-pay", { task_id: id, kind });
      setTask(data);
      const labels = {
        service_fee: "Service fee in escrow.",
        item_cost: "Item cost in escrow.",
        bundle: "Service fee + item cost funded — all in escrow.",
      };
      toast.success(labels[kind] || "Funds in escrow.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const onStripePay = async (kind) => {
    setBusy(true);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/payments/stripe/checkout", {
        task_id: id,
        kind,
        origin_url: origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err));
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen">
      <NavBar />
      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10 grid lg:grid-cols-12 gap-8">
        <TaskHeaderCard task={task} />

        <section className="lg:col-span-8 space-y-6">
          <TaskActionBar
            task={task}
            role={role}
            busy={busy}
            onFundEscrow={onFundEscrow}
            onStripePay={onStripePay}
            onStatusChange={onStatusChange}
          />

          {role === "admin" && (
            <AdminAssignAgentCard task={task} onTaskUpdate={setTask} />
          )}

          {task.agent_id && (isClient || role === "admin") && (
            <AgentTrustCard agentId={task.agent_id} agentName={task.agent_name} variant="panel" />
          )}

          {(isClient || role === "admin") && (
            <InvoiceCard task={task} />
          )}

          <ReferencePhotos task={task} role={role} isClient={isClient} wsSignal={refPhotosSignal} />

          <div className="eb-card p-6 md:p-8">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-2xl">Photo timeline</h2>
              <span className="text-xs text-[var(--eb-text-dim)]">
                {photos.length} update{photos.length === 1 ? "" : "s"}
              </span>
            </div>
            <PhotoTimeline photos={photos} />
            {isAssignedAgent && AGENT_LIVE_STATUSES.includes(task.status) && (
              <PhotoUploader
                taskId={task.task_id}
                onAdded={(ph) => setPhotos((prev) => [...prev, ph])}
              />
            )}
          </div>

          {isClient && task.status === "completed" && !task.rating && (
            <RatingPanel
              taskId={task.task_id}
              onDone={(rating) => setTask({ ...task, rating })}
            />
          )}

          {task.status === "completed" && (
            <ReceiptCard task={task} onTaskUpdate={setTask} />
          )}

          {isClient && <DisputeCard task={task} onTaskUpdate={setTask} />}
          {task.rating && (
            <div className="eb-card p-6">
              <div className="text-xs uppercase tracking-[0.2em] text-[var(--eb-text-dim)]">Rating</div>
              <div className="mt-2 flex items-center gap-1 text-[var(--eb-primary)]">
                {Array.from({ length: task.rating.stars }, (_, i) => `r${i + 1}`).map((rk) => (
                  <Star key={rk} className="w-5 h-5 fill-current" />
                ))}
              </div>
              {task.rating.comment && (
                <p className="mt-3 text-[var(--eb-text)]">&quot;{task.rating.comment}&quot;</p>
              )}
            </div>
          )}
        </section>

        <aside className="lg:col-span-4">
          <ChatPanel
            messages={messages}
            onSent={(m) => setMessages((prev) => [...prev, m])}
            taskId={task.task_id}
            wsRef={wsRef}
            currentUser={user}
          />
        </aside>

        {error && <div className="lg:col-span-12 text-[var(--eb-secondary)] text-sm">{error}</div>}
      </main>
    </div>
  );
}
